import { Component } from '@angular/core';
import { StatisticCardComponent } from '../shared/components/statistic-card/statistic-card.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ClickOutsideDirective } from 'app/shared/directives/click-outside.directive';
import { CreateCasePopupComponent } from 'app/shared/components/create-case-popup/create-case-popup.component';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { NewsPreviewComponent } from 'app/shared/components/news-preview/news-preview.component';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { DASHBOARD_TABLE_CONFIG, DASHBOARD_TABLE_STATE } from './home-dashboard-table.config';
import { CommonModule } from '@angular/common';
import { DataTableColumnFilters, DataTableColumnFiltersApi, DataTableColumnModel, DataTableConfigSettings, DataTableStateModel } from 'app/shared/components/dynamic-table/dynamic-table.model';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { HomeDashboardService } from './home-dashboard.service';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { DashboardCaseStatusEnum } from 'app/shared/enums/dashboard-case-status.enum';
import { NewsArticle } from 'app/profile/news/news-profile.model';
import { NewsService } from 'app/profile/news/news-profile.service';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { GlobalSearchComponent } from 'app/shared/components/global-search/global-search.component';
import { HelperService } from 'app/shared/services/helper.service';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

@Component({
  selector: 'app-home-dashboard',
  standalone: true,
  imports: [
    StatisticCardComponent,
    CreateCasePopupComponent,
    CaseFormSectionContainerComponent,
    NewsPreviewComponent,
    TranslateModule,
    ClickOutsideDirective,
    RouterLink,
    CommonModule,
    DynamicTableComponent,
    TitleHeaderComponent,
    GlobalSearchComponent
  ],
  templateUrl: './home-dashboard.component.html',
  styleUrl: './home-dashboard.component.scss'
})
export class HomeDashboardComponent {
  private readonly _destroy$ = new Subject<void>();
  news: NewsArticle[] = [];
  totalCount: number = 0;
  cardsList: any = [];
  columns = this.helperService.deepCopy(DASHBOARD_TABLE_CONFIG);
  cases: any = [];
  datatableDisplayAllNames: DataTableColumnFilters[] = [];
  isDataTableReady: boolean = false;
  filters: DataTableColumnFiltersApi[] = [];
  dashboardDatatableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DASHBOARD_TABLE_STATE.gridName);
  dashboardDataTableState: DataTableStateModel = this.dashboardDatatableStateFromStorage ? this.dashboardDatatableStateFromStorage : { ...DASHBOARD_TABLE_STATE };
  defaultColumns = this.helperService.deepCopy(DASHBOARD_TABLE_CONFIG);
  selectedFilterName = '';

  constructor(
    private newsService: NewsService,
    private homeDashboardService: HomeDashboardService,
    private router: Router,
    private configurationService: ConfigurationService,
    private helperService: HelperService,
    private activeRoute: ActivatedRoute,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.activeRoute.queryParams
      .pipe(takeUntil(this._destroy$))
      .subscribe((params) => {
        const card: string = params['card'];
        if (card && Object.keys(DashboardCaseStatusEnum).some((key: string) => (DashboardCaseStatusEnum as any)[key] === card)) {
          this.dashboardDataTableState.tableType = card;
        }
        this.setSelectedCardToUrlQueryParams(this.dashboardDataTableState.tableType);
      });
    this.getNews();
    this.getDataTableConfiguration();
  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.dashboardDataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          this.setData();
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.dashboardDatatableStateFromStorage) {
          this.columns = this.dashboardDataTableState.datatableColumns;
          this.filters = this.dashboardDataTableState.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.dashboardDataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.dashboardDataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }
        if (res && res.name) {
          this.selectedFilterName = res.name;
        }
        this.getStatisticCaseCards();
      })
  }

  setData() {
    this.setDataTableColumnFilters();
    this.datatableDisplayAllNames = [
      ...this.configurationService.getStatusItems(),
      ...this.configurationService.getNextActionStatusItemsForFilter(),
      ...this.configurationService.getCaseStageItems(),
      ...this.configurationService.getBooleanDataTableColumnFilters(),
      ...this.configurationService.getSeverityItems(),
    ];

    this.isDataTableReady = true;
    this.getCases();
  }

  setSelectedCardToUrlQueryParams(selectedCard: string) {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { card: selectedCard },
      queryParamsHandling: 'merge'
    });
  }

  setDataTableColumnFilters() {
    this.columns.forEach((column: any) => {
      switch (column.prop) {
        case 'caseStatus':
          column.dataTableColumnFilters = this.configurationService.getStatusItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
        case 'caseStage':
          column.dataTableColumnFilters = this.configurationService.getCaseStageItems();
          break;
        case 'severity':
          column.dataTableColumnFilters = this.configurationService.getSeverityItems();
          break;
      }
    });

    this.defaultColumns.forEach((column: any) => {
      switch (column.prop) {
        case 'caseStatus':
          column.dataTableColumnFilters = this.configurationService.getStatusItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
        case 'caseStage':
          column.dataTableColumnFilters = this.configurationService.getCaseStageItems();
          break;
        case 'severity':
          column.dataTableColumnFilters = this.configurationService.getSeverityItems();
          break;
      }
    });
  }

  getNews(): void {
    const params = {
      OrderField: 'Id',
      IsOrderByDescending: true,
      TakeCount: 5
    };

    this.newsService.getAllNews(params).subscribe((data: NewsArticle[]) => {
      this.news = data.map(article => ({
        ...article,
        text: article.shortBody || article.body,
        author: article.autorName,
        views: article.viewsCount,
        dateTime: article.publishDate ? new Date(article.publishDate) : new Date(),
      }));
    });
  }

  getCases() {
    this.cases = [];

    this.homeDashboardService.getElkDashboardList(this.dashboardDataTableState)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.cases = res.items.map((c: any) => ({
          ...c,
          externalCellClass: this.helperService.getColorOfNextActionDueDate(c.nextActionDueDate)
        }));

        this.totalCount = res.totalCount;
      });
  }

  getStatisticCaseCards(): void {
    this.cases = [];

    this.homeDashboardService
      .getStatisticCaseCards(this.dashboardDataTableState)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.setStatisticCaseCardsData(res);
      });
  }

  setStatisticCaseCardsData(data: any) {
    this.cardsList = []
    data.forEach((element: any) => {
      switch (element.type) {
        case DashboardCaseStatusEnum.MY_OPEN_CASES:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.my_open_cases',
            iconClass: 'bi bi-list-task',
            color: '#E6BA00',
          });
          break;
        case DashboardCaseStatusEnum.MY_NEXT_ACTIONS:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.my_next_actions',
            iconClass: 'bi bi-bullseye',
            color: '#000000',
          });
          break;
        case DashboardCaseStatusEnum.MY_OWNED_CASES:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.my_owned_cases',
            iconClass: 'bi bi-person-circle',
            color: '#198754',
          });
          break;
        case DashboardCaseStatusEnum.ESCALATED_CASES:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.escalated_cases',
            iconClass: 'bi bi-exclamation-triangle',
            color: '#CF202E',
          });
          break;
        case DashboardCaseStatusEnum.ALL_CASES:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.all_cases',
            iconClass: 'bi bi-infinity',
            color: '#A6AAAB',
          });
          break;
      }
    });
  }

  getDataTableState(data: any) {
    this.dashboardDataTableState = data;
    this.getCases();
    this.getStatisticCaseCards();
  }

  getActiveType(data: string) {
    this.dashboardDataTableState.tableType = data;
    this.dashboardDataTableState.pageNumber = 1;
    this.dashboardDataTableState.datatableColumns = this.columns;
    this.setSelectedCardToUrlQueryParams(this.dashboardDataTableState.tableType);
    this.getCases();
    this.getStatisticCaseCards();
    this.helperService.storeGridFilterObjectToSessionStorage(this.dashboardDataTableState.gridName, this.dashboardDataTableState);
  }

  getLinkedValue(data: any) {
    switch (data.column) {
      case 'caseIdentifier':
        this.router.navigate(['case-form/' + data.row.id]);
        break;
      case 'problemRecord':
        this.router.navigate(['problem-record/' + data.row.problemRecordId]);
        break;
    }
  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);
    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dashboardDataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || '',
    }

    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe();
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dashboardDataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }

  openCreateCasePopup() {
    this.helperService.createCasePopoupVisible = true;
    this.helperService.isSidebar = false;
  }
}
